import React, { useState, useEffect, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Typography, Grid, Box, Avatar, Button, Pagination, Skeleton, Tabs, Tab, Chip, Link, Tooltip, IconButton, Snackbar } from '@mui/material';
import FmdGoodOutlinedIcon from '@mui/icons-material/FmdGoodOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import BaseDialog from '../components/dialogs/BaseDialog';
import LinkIcon from '@mui/icons-material/Link';
import { getUserProfile, updateUserProfile } from '../services/api';
import { UserProfile } from '../types/UserProfile';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import { SITE_NAME } from '../constants/site';
import OnlineStatus from '../components/OnlineStatus';
import GameListComponent from '../components/GameList/GameListComponent';
import CreateGameList from '../components/GameList/CreateGameList';
import { getUserGameLists, GameListSummary, GameList, deleteGameList } from '../services/contents_api';
import EditGameList from '../components/GameList/EditGameList';
import ShareButtons from '../components/common/ShareButtons';
import { isAuthenticated } from '../services/auth';
import ActivityTab from '../components/profile/Activity';
import ProfileTab from '../components/ProfileTab';
import AnimeProfileTab from '../components/profile/anime/AnimeProfileTab';
import MovieProfileTab from '../components/profile/movie/MovieProfileTab';
import SettingsIcon from '@mui/icons-material/Settings';
import GetAppIcon from '@mui/icons-material/GetApp';
import Expandable from '../components/common/Expandable';
import { isPwa } from '../utils/isPwa';
import ReviewsTab from '../components/ReviewsTab';
import AnimeReviewsTab from '../components/AnimeReviewsTab';
import MovieReviewsTab from '../components/MovieReviewsTab';
import MemoriesTab from '../components/MemoriesTab';
import PostFeed from '../components/posts/PostFeed';
import FollowButton from '../components/common/FollowButton';
import StyledButton from '../components/common/StyledButton';
import UserPlanBadge from '../components/common/UserPlanBadge';
import FollowsTab from '../components/FollowsTab';
import TimelineTab from '../components/TimelineTab';
import BoltIcon from '@mui/icons-material/Bolt';
import PlayLogsTab from '../components/PlayLogsTab';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import GotyTab from '../components/GotyTab';
import SocialLinks from '../components/profile/SocialLinks';
import { getSteamPlayerSummary, SteamPlayerSummary, getGameIdBySteamId } from '../services/SteamApi';
import GameLibraryTab from '../components/library/GameLibraryTab';
import AnimeLibraryTab from '../components/library/AnimeLibraryTab';
import MovieLibraryTab from '../components/library/MovieLibraryTab';
import GalleryTab from '../components/gallery/GalleryTab';
import SportsEsportsIcon from '@mui/icons-material/SportsEsports';
import LiveTvIcon from '@mui/icons-material/LiveTv';
import MovieIcon from '@mui/icons-material/Movie';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import EditNoteIcon from '@mui/icons-material/EditNote';

type MediaType = 'game' | 'anime' | 'movie';

const MEDIA_ITEMS: { value: MediaType; label: string; icon: React.ReactNode }[] = [
  { value: 'game', label: 'ゲーム', icon: <SportsEsportsIcon sx={{ fontSize: '0.9rem' }} /> },
  { value: 'anime', label: 'アニメ', icon: <LiveTvIcon sx={{ fontSize: '0.9rem' }} /> },
  { value: 'movie', label: '映画', icon: <MovieIcon sx={{ fontSize: '0.9rem' }} /> },
];

// 拡張ライブラリ(アニメ/映画)の切替バーを自分で非表示にしたか。端末ローカル設定。
// 非表示にできるのは拡張ライブラリのデータが無いときだけなので、1件でも登録すれば
// このフラグに関わらずバーは再表示される（＝データを持つのにバーが無い状態は作れない）。
const EXT_BAR_HIDDEN_KEY = 'extLibraryBarHidden';

const MediaSegmentedControl: React.FC<{
  value: MediaType;
  onChange: (e: React.SyntheticEvent, v: MediaType) => void;
  items?: typeof MEDIA_ITEMS;
}> = ({ value, onChange, items = MEDIA_ITEMS }) => {
  const { t } = useTranslation('common');
  const containerRef = React.useRef<HTMLDivElement>(null);
  const itemRefs = React.useRef<(HTMLDivElement | null)[]>([]);
  const [indicator, setIndicator] = React.useState<{ left: number; width: number } | null>(null);
  const mounted = React.useRef(false);

  React.useLayoutEffect(() => {
    const activeIndex = items.findIndex(i => i.value === value);
    const el = itemRefs.current[activeIndex];
    if (el) {
      setIndicator({ left: el.offsetLeft, width: el.offsetWidth });
    }
    mounted.current = true;
  }, [value, items]);

  return (
    <Box
      ref={containerRef}
      sx={{
        display: 'inline-flex',
        position: 'relative',
        backgroundColor: 'rgba(255, 255, 255, 0.03)',
        borderRadius: '8px',
        p: '3px',
        mb: 1.5,
        mt: -1,
        border: '1px solid rgba(255, 255, 255, 0.1)',
      }}
    >
      {indicator && (
        <Box
          sx={{
            position: 'absolute',
            top: '3px',
            left: `${indicator.left}px`,
            width: `${indicator.width}px`,
            height: 'calc(100% - 6px)',
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
            borderRadius: '6px',
            transition: mounted.current
              ? 'left 0.2s cubic-bezier(0.4, 0, 0.2, 1), width 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
              : 'none',
            pointerEvents: 'none',
          }}
        />
      )}
      {items.map((item, i) => (
        <Box
          key={item.value}
          ref={(el: HTMLDivElement | null) => { itemRefs.current[i] = el; }}
          onClick={(e) => onChange(e, item.value)}
          sx={{
            position: 'relative',
            zIndex: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            px: { xs: 1.2, sm: 2 },
            py: { xs: 0.25, sm: 0.25 },
            borderRadius: '6px',
            fontSize: { xs: '0.7rem', sm: '0.75rem' },
            fontWeight: value === item.value ? 500 : 400,
            cursor: 'pointer',
            transition: 'color 0.2s ease',
            color: value === item.value ? 'text.secondary' : 'rgba(255, 255, 255, 0.5)',
            userSelect: 'none',
            '&:hover': {
              color: value === item.value ? 'text.secondary' : 'rgba(255, 255, 255, 0.7)',
            },
          }}
        >
          {item.icon}
          {t(`media.${item.value}`, { defaultValue: item.label })}
        </Box>
      ))}
    </Box>
  );
};

interface ProfilePageProps {
  profile?: UserProfile;
  isPublic?: boolean;
  isOwnProfile?: boolean;
}

// タブの種類を定数で定義
const MAIN_TABS = {
  PROFILE: 'profile',
  LIBRARY: 'library',
  LISTS: 'lists',
  REVIEWS: 'reviews',
  MEMORIES: 'memories',
  ACTIVITY: 'activity',
  FOLLOWS: 'follows',
  TIMELINE: 'timeline',
  PLAYLOGS: 'playlogs',
  POSTS: 'posts',
  GOTY: 'goty',
  GALLERY: 'gallery',
} as const;

type MainTabType = typeof MAIN_TABS[keyof typeof MAIN_TABS];

// メインタブが横スクロールできる側にかけるフェードの幅(px)
const TAB_FADE_WIDTH = 32;

const ProfilePage: React.FC<ProfilePageProps> = ({ 
  profile: initialProfile, 
  isPublic = false,
  isOwnProfile = false
}) => {
  const { t } = useTranslation('profile');
  const navigate = useNavigate();
  const location = useLocation();
  const { username } = useParams<{ username: string }>();
  const queryParams = new URLSearchParams(location.search);
  
  // 初期値の設定を修正
  const tab = queryParams.get('tab') as MainTabType;
  const initialMedia = (queryParams.get('media') as MediaType)
    || (!isPublic ? (localStorage.getItem('libraryMediaTab') as MediaType) : null)
    || 'game';

  const [mainTabValue, setMainTabValue] = useState<MainTabType>(
    tab || (isPublic ? MAIN_TABS.PROFILE : MAIN_TABS.LIBRARY)
  );
  const [mediaType, setMediaType] = useState<MediaType>(initialMedia);
  const [extBarHidden, setExtBarHidden] = useState<boolean>(
    () => localStorage.getItem(EXT_BAR_HIDDEN_KEY) === '1'
  );
  const [extInfoOpen, setExtInfoOpen] = useState(false);

  const queryClient = useQueryClient();
  const ownProfileEnabled = !isPublic && !initialProfile;

  // 自分のプロフィールは React Query でキャッシュ取得（再訪問は即時描画、鮮度はバックグラウンド更新）。
  const { data: queriedProfile, isError: profileFetchError } = useQuery({
    queryKey: ['profile', 'me'],
    queryFn: async () => (await getUserProfile()).data,
    enabled: ownProfileEnabled,
    staleTime: 10_000,
  });

  // 初期値：public は親の initialProfile、自分はキャッシュ済みがあれば即採用（初回のチラ見え防止）。
  const seedProfile = initialProfile
    || (ownProfileEnabled ? queryClient.getQueryData<UserProfile>(['profile', 'me']) : undefined)
    || null;

  // FollowButton 等のローカル楽観更新と両立させるため、表示は作業コピー(profile)を使う。
  const [profile, setProfile] = useState<UserProfile | null>(seedProfile);
  const [isEditing, setIsEditing] = useState(false);
  const [loadingProfile, setLoadingProfile] = useState<boolean>(!seedProfile);
  const [gameCounts, setGameCounts] = useState<number[]>(seedProfile?.status_counts || [0, 0, 0, 0, 0]);
  const [animeCounts, setAnimeCounts] = useState<number[]>(seedProfile?.anime_status_counts || [0, 0, 0, 0, 0]);
  const [movieCounts, setMovieCounts] = useState<number[]>(seedProfile?.movie_status_counts || [0, 0, 0, 0, 0]);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [createListDialogOpen, setCreateListDialogOpen] = useState(false);
  const [editingList, setEditingList] = useState<GameList | null>(null);
  const initialListPage = parseInt(queryParams.get('list_page') || '1', 10);
  const [listPage, setListPage] = useState(initialListPage);
  const [steamStatus, setSteamStatus] = useState<SteamPlayerSummary | null>(null);

  const scrollPositionRef = useRef<number>(0);

  // メインタブの横スクロール。MUIの矢印(TabScrollButton)は無効時も幅を占有して
  // 端に死んだ余白を作るため使わず、スクロール可能な側だけをフェードさせて示す。
  const tabsBoxRef = useRef<HTMLDivElement>(null);
  const [tabFade, setTabFade] = useState({ left: false, right: false });

  useEffect(() => {
    const scroller = tabsBoxRef.current?.querySelector<HTMLDivElement>('.MuiTabs-scroller');
    if (!scroller) return;

    const update = () => {
      const { scrollLeft, scrollWidth, clientWidth } = scroller;
      // 端の判定は小数のズレを拾わないよう1px余裕を持たせる
      setTabFade({
        left: scrollLeft > 1,
        right: scrollLeft + clientWidth < scrollWidth - 1,
      });
    };

    update();
    scroller.addEventListener('scroll', update, { passive: true });
    // 表示幅(scroller)とタブ列の実寸(flexContainer)の両方を見る。
    // 後者はラベルの文字幅が後から変わる場合(i18n差し替え等)に効く。
    const observer = new ResizeObserver(update);
    observer.observe(scroller);
    const flexContainer = scroller.querySelector('.MuiTabs-flexContainer');
    if (flexContainer) observer.observe(flexContainer);

    return () => {
      scroller.removeEventListener('scroll', update);
      observer.disconnect();
    };
  }, [mediaType, isPublic]);

  // タブ列をマウスドラッグで横スクロールできるようにする(PC向け)。
  // 矢印を消したぶん、PCでの横移動の手段を用意する。
  // タッチは元々ネイティブの横スワイプが効くので mouse のときだけ介入する。
  useEffect(() => {
    const scroller = tabsBoxRef.current?.querySelector<HTMLDivElement>('.MuiTabs-scroller');
    if (!scroller) return;

    // この距離を超えて動かして初めて「ドラッグ」と見なす。
    // 超えるまでは何もしないので、通常のクリックでのタブ切替を邪魔しない。
    const DRAG_THRESHOLD_PX = 5;

    let activePointerId: number | null = null;
    let startClientX = 0;
    let startScrollLeft = 0;
    let isDragging = false;

    const handlePointerDown = (event: PointerEvent) => {
      if (event.pointerType !== 'mouse' || event.button !== 0) return;
      if (scroller.scrollWidth <= scroller.clientWidth) return; // スクロールの余地なし
      activePointerId = event.pointerId;
      startClientX = event.clientX;
      startScrollLeft = scroller.scrollLeft;
      isDragging = false;
    };

    const handlePointerMove = (event: PointerEvent) => {
      if (activePointerId === null || event.pointerId !== activePointerId) return;
      const dx = event.clientX - startClientX;

      if (!isDragging) {
        if (Math.abs(dx) < DRAG_THRESHOLD_PX) return;
        isDragging = true;
        scroller.setPointerCapture(activePointerId);
        scroller.style.cursor = 'grabbing';
        scroller.style.userSelect = 'none'; // ドラッグ中にラベルが選択されるのを防ぐ
      }

      event.preventDefault();
      scroller.scrollLeft = startScrollLeft - dx;
    };

    const handlePointerUp = (event: PointerEvent) => {
      if (activePointerId === null || event.pointerId !== activePointerId) return;

      if (isDragging) {
        if (scroller.hasPointerCapture(activePointerId)) {
          scroller.releasePointerCapture(activePointerId);
        }
        scroller.style.cursor = '';
        scroller.style.userSelect = '';

        // pointerup の直後に飛ぶ click を1回だけ握り潰す。
        // これが無いと、指を離した位置にあるタブが選択されてしまう。
        // click が飛ばずに終わる場合もあるので、次のタスクで必ず外す。
        const suppressClick = (clickEvent: MouseEvent) => {
          clickEvent.preventDefault();
          clickEvent.stopPropagation();
        };
        scroller.addEventListener('click', suppressClick, { capture: true, once: true });
        window.setTimeout(() => {
          scroller.removeEventListener('click', suppressClick, { capture: true });
        }, 0);
      }

      activePointerId = null;
      isDragging = false;
    };

    scroller.addEventListener('pointerdown', handlePointerDown);
    // 捕捉が始まる前にポインタが外へ出ても追随できるよう、move/up は window で受ける
    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);
    window.addEventListener('pointercancel', handlePointerUp);

    return () => {
      scroller.removeEventListener('pointerdown', handlePointerDown);
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
      window.removeEventListener('pointercancel', handlePointerUp);
    };
  }, [mediaType, isPublic]);

  // 端に達している側はフェードさせない（= 常時ぼかしっぱなしにしない）
  const tabFadeMask = `linear-gradient(to right,
    transparent 0px,
    #000 ${tabFade.left ? TAB_FADE_WIDTH : 0}px,
    #000 calc(100% - ${tabFade.right ? TAB_FADE_WIDTH : 0}px),
    transparent 100%)`;

  // スクロール位置を保存
  useEffect(() => {
    const handleScroll = () => {
      scrollPositionRef.current = window.scrollY;
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // 認証チェックを追加
  useEffect(() => {
    const checkAuth = async () => {
      if (!isPublic) {
        const authenticated = await isAuthenticated();
        if (!authenticated) {
          navigate('/login', { state: { from: location.pathname } });
        }
      }
    };

    checkAuth();
  }, [isPublic, navigate, location.pathname]);

  // クエリ結果（自分）or 親からの initialProfile（public）を作業用ローカルstateへ反映。
  useEffect(() => {
    const src = isPublic ? initialProfile : queriedProfile;
    if (src) {
      setProfile(src);
      setGameCounts(src.status_counts || [0, 0, 0, 0, 0]);
      setAnimeCounts(src.anime_status_counts || [0, 0, 0, 0, 0]);
      setMovieCounts(src.movie_status_counts || [0, 0, 0, 0, 0]);
      setLoadingProfile(false);
    }
  }, [isPublic, initialProfile, queriedProfile]);

  const hasExtData = animeCounts.some(c => c > 0) || movieCounts.some(c => c > 0);
  // 他人のプロフィールでは、相手が使っていないメディアの空タブに意味が無いのでデータがある時だけ出す。
  // 自分のプロフィールでは、拡張ライブラリの存在に気づけるよう常時表示し、不要なら自分で消せる。
  const showMediaBar = isPublic ? hasExtData : (hasExtData || !extBarHidden);

  // 保険: 切替バーが出ないのに mediaType が非ゲーム（localStorage/URL 由来の残存）だと、
  // ゲームライブラリへ戻れなくなる。バーが無い状態では game に戻す。
  useEffect(() => {
    if (loadingProfile || mediaType === 'game' || showMediaBar) return;
    setMediaType('game');
    if (!isPublic) localStorage.setItem('libraryMediaTab', 'game');
  }, [loadingProfile, mediaType, showMediaBar, isPublic]);

  const handleHideExtBar = () => {
    setExtBarHidden(true);
    localStorage.setItem(EXT_BAR_HIDDEN_KEY, '1');
    setExtInfoOpen(false);
  };

  // 取得失敗時もローディングを解除（旧 try/finally と同等。エラー時の無限ローディング防止）。
  useEffect(() => {
    if (ownProfileEnabled && profileFetchError) {
      setLoadingProfile(false);
    }
  }, [ownProfileEnabled, profileFetchError]);

  // Steamステータスを取得（steam_idがある場合）
  useEffect(() => {
    const fetchSteamStatus = async () => {
      if (profile?.steam_id) {
        const status = await getSteamPlayerSummary(profile.steam_id);
        setSteamStatus(status);
      }
    };
    fetchSteamStatus();
  }, [profile?.steam_id]);

  // Steamプレイ中ゲームのクリックハンドラー
  const handleSteamGameClick = async () => {
    if (!steamStatus?.gameid) return;

    // PULSEのgame_idを取得
    const pulseGameId = await getGameIdBySteamId(steamStatus.gameid);
    if (pulseGameId) {
      navigate(`/game/${pulseGameId}`);
    } else {
      setSnackbarMessage('データベースに登録されていないか、PULSEとSteamの連携が完了していないゲームです');
      setOpenSnackbar(true);
    }
  };

  const handleEditClick = () => {
    navigate('/profile/edit');
  };

  const handleSave = async (updatedProfile: UserProfile) => {
    try {
      const response = await updateUserProfile(updatedProfile);
      setIsEditing(false);
      setProfile(response.data);
      queryClient.setQueryData(['profile', 'me'], response.data); // キャッシュも最新化
    } catch (error: any) {
      if (error.response && error.response.data) {
        console.error('プロフィールの更新に失敗しました', error.response.data);
        throw error; // エラーを再スローして、ProfileEditFormで処理できるようにする
      }
    }
  };

  const handleCopyLink = () => {
    const publicProfileUrl = `${window.location.origin}/profile/${profile?.username}`;
    navigator.clipboard.writeText(publicProfileUrl);
    setSnackbarMessage('リンクがコピーされました');
    setOpenSnackbar(true);
  };

  // メインタブ変更ハンドラー
  const handleMainTabChange = (event: React.SyntheticEvent, newValue: MainTabType) => {
    const currentScroll = scrollPositionRef.current;
    setMainTabValue(newValue);

    const params = new URLSearchParams();
    params.set('tab', newValue);

    params.set('media', mediaType);
    if (newValue === MAIN_TABS.LISTS) {
      params.set('list_page', listPage.toString());
    }

    const newUrl = isPublic
      ? `/profile/${username}?${params.toString()}`
      : `/profile?${params.toString()}`;
    navigate(newUrl, { replace: true });

    requestAnimationFrame(() => {
      window.scrollTo(0, currentScroll);
    });

    const target = event.currentTarget as HTMLElement;
    if (target) {
      setTimeout(() => {
        target.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
      }, 0);
    }
  };

  // メディアタイプ切替ハンドラー
  const handleMediaChange = (_event: React.SyntheticEvent, newMedia: MediaType) => {
    setMediaType(newMedia);
    if (!isPublic) {
      localStorage.setItem('libraryMediaTab', newMedia);
    }

    // メディア切替時、対象メディアで利用不可のタブならライブラリにリセット
    const validNonGameTabs: MainTabType[] = [MAIN_TABS.PROFILE, MAIN_TABS.LIBRARY, MAIN_TABS.REVIEWS];
    let newTab = mainTabValue;
    if ((newMedia === 'anime' || newMedia === 'movie') && !validNonGameTabs.includes(mainTabValue)) {
      newTab = MAIN_TABS.LIBRARY;
      setMainTabValue(MAIN_TABS.LIBRARY);
    }

    const params = new URLSearchParams({
      tab: newTab,
      media: newMedia,
    });
    const newUrl = isPublic
      ? `/profile/${username}?${params.toString()}`
      : `/profile?${params.toString()}`;
    navigate(newUrl, { replace: true });
  };

  // React Query でゲームリストを取得
  const stableListUsername = isPublic ? username : 'me';
  const { data: gameListsData } = useQuery({
    queryKey: ['gameLists', stableListUsername, listPage],
    queryFn: () => getUserGameLists(isPublic ? username : undefined, listPage),
    enabled: mainTabValue === MAIN_TABS.LISTS,
    staleTime: isPublic ? 30000 : 2000,
  });

  const gameLists: GameListSummary[] = gameListsData?.results || [];
  const listTotalPages = Math.ceil((gameListsData?.count || 0) / 12);

  // リストの編集ハンドラー
  const handleEditList = (list: GameListSummary) => {
    setEditingList(list as unknown as GameList); // 一時的な型キャスト
  };

  const invalidateGameLists = () => {
    queryClient.invalidateQueries({ queryKey: ['gameLists', stableListUsername] });
  };

  // リストの削除ハンドラー
  const handleDeleteList = async (listId: number) => {
    if (window.confirm('このリストを削除してもよろしいですか？')) {
      try {
        await deleteGameList(listId);
        invalidateGameLists();
      } catch (error) {
        console.error('リストの削除に失敗しました:', error);
      }
    }
  };

  // URLのパラメータが変更されたときの処理
  useEffect(() => {
    const newTab = queryParams.get('tab') as MainTabType;
    const newMedia = (queryParams.get('media') as MediaType)
      || (!isPublic ? (localStorage.getItem('libraryMediaTab') as MediaType) : null)
      || 'game';
    const newListPage = parseInt(queryParams.get('list_page') || '1', 10);

    // メディアタイプに応じたタブのバリデーション
    const validNonGameTabs: MainTabType[] = [MAIN_TABS.PROFILE, MAIN_TABS.LIBRARY, MAIN_TABS.REVIEWS];
    let effectiveTab = newTab || (isPublic ? MAIN_TABS.PROFILE : MAIN_TABS.LIBRARY);
    if ((newMedia === 'anime' || newMedia === 'movie') && !validNonGameTabs.includes(effectiveTab)) {
      effectiveTab = MAIN_TABS.LIBRARY;
    }

    if (effectiveTab !== mainTabValue) {
      setMainTabValue(effectiveTab);
    }

    if (newMedia !== mediaType) {
      setMediaType(newMedia);
    }

    if (newTab === MAIN_TABS.LISTS && newListPage !== listPage) {
      setListPage(newListPage);
    }
  }, [location.search]);

  // リストページ変更ハンドラーを修正
  const handleListPageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setListPage(value);
    // URLを更新
    const params = new URLSearchParams(queryParams);
    params.set('list_page', value.toString());
    params.set('tab', MAIN_TABS.LISTS);
    
    const newUrl = isPublic
      ? `/profile/${username}?${params.toString()}`
      : `/profile?${params.toString()}`;
    navigate(newUrl, { replace: true });
    
    // ページトップにスクロール
    window.scrollTo(0, 0);
  };

  return (
    <>
      <Helmet>
        <title>
          {isPublic && profile 
            ? `${profile.display_name || profile.username}(@${profile.username})のゲームライブラリ | ${SITE_NAME}`
            : `マイページ | ${SITE_NAME}`}
        </title>
      </Helmet>
      
      <Grid container spacing={3} sx={{ mb: { xs: 2, md: 2 } }}>
        <Grid item xs={12} md={8}>
          {loadingProfile ? (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Skeleton variant="circular" width={56} height={56} />
              <Box sx={{ ml: 2, flexGrow: 1 }}>
                <Skeleton width="40%" height={32} />
                <Skeleton width="60%" />
                <Skeleton width="80%" />
              </Box>
            </Box>
          ) : (
            profile && (
              <>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center',
                  mb: 1
                }}>
                  <Avatar
                    src={profile.avatar || undefined}
                    sx={{ width: 56, height: 56 }}
                  />
                  <Box sx={{ ml: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                      <Typography variant="h6" component="h1">{profile.display_name || profile.username}</Typography>
                      <UserPlanBadge planType={profile.subscription_plan} />
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <Typography variant="body2" color="text.secondary">
                        @{profile.username}
                      </Typography>
                      {isPublic && profile.last_access && !profile.current_playing_game_id && !steamStatus?.gameextrainfo && (
                        <OnlineStatus lastAccess={profile.last_access} />
                      )}
                      {/* Steamでプレイ中のゲーム表示 */}
                      {steamStatus?.gameextrainfo && !profile.current_playing_game_id && (
                        <Box
                          onClick={handleSteamGameClick}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            backgroundColor: 'rgba(102, 192, 244, 0.12)',
                            border: '1px solid rgba(102, 192, 244, 0.5)',
                            borderRadius: '4px',
                            padding: '1px 8px',
                            paddingLeft: '4px',
                            textDecoration: 'none',
                            position: 'relative',
                            overflow: 'hidden',
                            cursor: steamStatus.gameid ? 'pointer' : 'default',
                            '&:hover': {
                              opacity: steamStatus.gameid ? 0.8 : 1
                            },
                            '&::after': {
                              content: '""',
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              right: 0,
                              bottom: 0,
                              background: 'linear-gradient(90deg, rgba(102, 192, 244, 0), rgba(102, 192, 244, 0.2), rgba(102, 192, 244, 0))',
                              backgroundSize: '200% 100%',
                              animation: 'shimmer 2s infinite',
                            },
                            '@keyframes shimmer': {
                              '0%': {
                                backgroundPosition: '100% 0'
                              },
                              '100%': {
                                backgroundPosition: '-100% 0'
                              }
                            }
                          }}
                        >
                          <Box
                            sx={{
                              width: '0.85rem',
                              height: '0.85rem',
                              maskImage: 'url(/icons/service/steam_black.svg)',
                              maskSize: 'contain',
                              maskRepeat: 'no-repeat',
                              maskPosition: 'center',
                              WebkitMaskImage: 'url(/icons/service/steam_black.svg)',
                              WebkitMaskSize: 'contain',
                              WebkitMaskRepeat: 'no-repeat',
                              WebkitMaskPosition: 'center',
                              backgroundColor: '#e6e6e6',
                              zIndex: 1,
                            }}
                          />
                          <Typography
                            variant="caption"
                            sx={{
                              color: '#e6e6e6',
                              fontSize: '0.7rem',
                              fontWeight: 400,
                              lineHeight: '1',
                              letterSpacing: '0.02em',
                              maxWidth: '180px',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              zIndex: 1,
                            }}
                          >
                            {steamStatus.gameextrainfo}
                          </Typography>
                        </Box>
                      )}
                      {profile.current_playing_game_id && (
                        <Box
                          onClick={() => navigate(`/game/${profile.current_playing_game_id}`)}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            backgroundColor: 'rgba(217, 255, 0, 0.12)',
                            border: '1px solid rgba(217, 255, 0, 0.5)',
                            borderRadius: '4px',
                            padding: '1px 8px',
                            paddingLeft: '4px',
                            position: 'relative',
                            overflow: 'hidden',
                            cursor: 'pointer',
                            '&:hover': {
                              opacity: 0.8
                            },
                            '&::after': {
                              content: '""',
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              right: 0,
                              bottom: 0,
                              background: 'linear-gradient(90deg, rgba(217, 255, 0, 0), rgba(217, 255, 0, 0.2), rgba(217, 255, 0, 0))',
                              backgroundSize: '200% 100%',
                              animation: 'shimmer 2s infinite',
                            },
                            '@keyframes shimmer': {
                              '0%': {
                                backgroundPosition: '100% 0'
                              },
                              '100%': {
                                backgroundPosition: '-100% 0'
                              }
                            }
                          }}
                        >
                          <PlayArrowIcon 
                            sx={{ 
                              fontSize: '0.875rem',
                              color: '#d9ff00',
                            }} 
                          />
                          <Typography
                            variant="caption"
                            sx={{
                              color: '#d9ff00',
                              fontSize: '0.7rem',
                              fontWeight: 400,
                              lineHeight: '1',
                              letterSpacing: '0.02em',
                              zIndex: 1
                            }}
                          >
                            now playing
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Box>
                </Box>
                {profile.bio && (
                  <Expandable maxLines={3} expandButtonStyle="icon">
                    <Typography variant="body2" sx={{ mb: 0.5, lineHeight: 1.25, whiteSpace: 'pre-wrap' }}>
                      {profile.bio}
                    </Typography>
                  </Expandable>
                )}
                {isPublic && profile.location && (
                  <Box sx={{
                    mb: 0.5,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    color: 'text.secondary',
                  }}>
                    <FmdGoodOutlinedIcon sx={{ fontSize: '1rem' }} />
                    <Typography variant="body2" sx={{ fontSize: '0.75rem'}}>{profile.location}</Typography>
                  </Box>
                )}
                {profile.social_links && Object.keys(profile.social_links).length > 0 && (
                  <SocialLinks
                    socialLinks={profile.social_links}
                    onCopy={(message) => {
                      setSnackbarMessage(message);
                      setOpenSnackbar(true);
                    }}
                  />
                )}
                {!isPublic && (
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1, flexWrap: 'wrap' }}>
                      <Link
                        href={`/profile/${profile.username}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ textDecoration: 'none' }}
                      >
                        <Chip
                          icon={<LinkIcon sx={{ fontSize: '0.875rem !important' }} />}
                          label={`公開URL: /profile/${profile.username}`}
                          size="small"
                          sx={{
                            height: '24px',
                            fontSize: '0.75rem',
                            backgroundColor: 'rgba(255, 255, 255, 0.05)',
                            color: 'text.secondary',
                            cursor: 'pointer',
                            '&:hover': {
                              backgroundColor: 'rgba(255, 255, 255, 0.08)',
                              borderColor: 'primary.main',
                            },
                            '& .MuiChip-icon': {
                              marginLeft: '6px',
                              color: 'text.secondary'
                            }
                          }}
                        />
                      </Link>
                      {/* ゲストのプロフィールは公開されない（共有しても相手には見えない）。
                          登録の動機付けとしてボタンは残し、押されたら登録促しダイアログを出す */}
                      <Box
                        onClickCapture={profile.is_guest ? (e: React.MouseEvent) => {
                          e.preventDefault();
                          e.stopPropagation();
                          window.dispatchEvent(new CustomEvent('guestUpgradeRequired'));
                        } : undefined}
                        sx={{ display: 'flex' }}
                      >
                        <ShareButtons
                          text={`${profile.display_name || profile.username}のゲームライブラリ | ${SITE_NAME} - ゲーム記録管理プラットフォーム`}
                          url={`/profile/${profile.username}`}
                          hashtags={['PULSE_DB']}
                        />
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<SettingsIcon />}
                        onClick={() => navigate('/profile/settings')}
                        sx={{
                          color: 'text.primary',
                          backgroundColor: 'rgba(255, 255, 255, 0.04)',
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: '50px',
                          '&:hover': {
                            backgroundColor: 'action.hover',
                            borderColor: 'primary.main',
                          }
                        }}
                      >
                        {t('settingsButton')}
                      </Button>
                      {/* 必要性検討中につき一旦非表示
                      {hasEarlyAccess && (
                        <Tooltip title="ポストを書く">
                          <IconButton
                            size="small"
                            onClick={() => {
                              setMainTabValue(MAIN_TABS.POSTS);
                              const params = new URLSearchParams({ tab: 'posts', compose: '1' });
                              navigate(`/profile?${params.toString()}`, { replace: true });
                            }}
                            sx={{
                              border: '1px solid',
                              borderColor: 'divider',
                              backgroundColor: 'rgba(255, 255, 255, 0.04)',
                              '&:hover': {
                                backgroundColor: 'action.hover',
                                borderColor: 'primary.main',
                              }
                            }}
                          >
                            <EditNoteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      */}
                      {!isPwa() && (
                        <Tooltip title="PWAアプリをインストール">
                          <IconButton
                            size="small"
                            onClick={() => navigate('/install-pwa')}
                            sx={{ 
                              border: '1px solid',
                              borderColor: 'divider',
                              backgroundColor: 'rgba(255, 255, 255, 0.04)',
                              '&:hover': {
                                backgroundColor: 'action.hover',
                                borderColor: 'primary.main',
                              }
                            }}
                          >
                            <GetAppIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </Box>
                )}
                {isPublic && (
                  <Box sx={{ mt: 1 }}>
                    <FollowButton
                      username={profile.username}
                      isFollowing={profile.is_following}
                      isOwnProfile={isOwnProfile}
                      isFollowedBy={profile.is_followed_by}
                      onFollowChange={(isFollowing) => {
                        setProfile(prev => prev ? {
                          ...prev,
                          is_following: isFollowing,
                          followers_count: prev.followers_count + (isFollowing ? 1 : -1)
                        } : null);
                      }}
                    />
                  </Box>
                )}
              </>
            )
          )}
        </Grid>
        <Grid item xs={12} md={4} sx={{ display: { xs: 'none', md: 'block' } }}>
          {/* 右側のカラムに追加のコンテンツを配置できます */}
        </Grid>
      </Grid>

      {/* メディアタイプ切替。ⓘ は拡張ライブラリ未使用のときだけ（＝説明が要る人にだけ）出す。 */}
      {showMediaBar && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <MediaSegmentedControl value={mediaType} onChange={handleMediaChange} />
          {!isPublic && !hasExtData && (
            <Tooltip title={t('extLibrary.iconTooltip')}>
              <IconButton
                size="small"
                onClick={() => setExtInfoOpen(true)}
                sx={{
                  mt: -1,
                  mb: 1.5,
                  color: 'rgba(255, 255, 255, 0.4)',
                  '&:hover': { color: 'rgba(255, 255, 255, 0.7)' },
                }}
              >
                <InfoOutlinedIcon sx={{ fontSize: '1rem' }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      )}

      <BaseDialog
        open={extInfoOpen}
        onClose={() => setExtInfoOpen(false)}
        title={t('extLibrary.title')}
        actions={
          <>
            <Button onClick={() => setExtInfoOpen(false)} size="small" sx={{ color: 'text.secondary' }}>
              {t('extLibrary.close')}
            </Button>
            <Button onClick={handleHideExtBar} size="small" color="error">
              {t('extLibrary.hide')}
            </Button>
          </>
        }
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Typography variant="body2" color="text.secondary">
            {t('extLibrary.body', { site: SITE_NAME })}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('extLibrary.hideNote')}
          </Typography>
        </Box>
      </BaseDialog>

      {/* メインタブ */}
      {(
      <Box ref={tabsBoxRef} sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs
          value={mainTabValue}
          onChange={handleMainTabChange}
          variant="scrollable"
          scrollButtons={false}
          sx={{
            minHeight: { xs: '32px', sm: '40px' },
            // スクロールできる側だけを透過させ、続きがあることを示す。
            // 下線(borderBottom)は親のBoxにあるのでフェードの影響を受けない。
            maskImage: tabFadeMask,
            WebkitMaskImage: tabFadeMask,
            '& .MuiTab-root': {
              minHeight: { xs: '36px', sm: '40px' },
              py: { xs: 1, sm: 1 },
              px: { xs: 1, sm: 1.5 },
              fontSize: { xs: '0.65rem', sm: '0.75rem' },
              minWidth: 0
            }
          }}
        >
          {!isPublic && mediaType === 'game' && (
            <Tab
              icon={
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 24,
                    height: 24,
                    borderRadius: '4px',
                    backgroundColor: 'rgba(255, 255, 255, 0.08)',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.12)'
                    }
                  }}
                >
                  <BoltIcon
                    sx={{
                      fontSize: '1rem',
                      transform: 'rotate(20deg)'
                    }}
                  />
                </Box>
              }
              value={MAIN_TABS.TIMELINE}
            />
          )}
          <Tab label={t('tabs.profile')} value={MAIN_TABS.PROFILE} />
          <Tab label={t('tabs.library')} value={MAIN_TABS.LIBRARY} />
          <Tab label={t('tabs.reviews')} value={MAIN_TABS.REVIEWS} />
          {mediaType === 'game' && <Tab label={t('tabs.posts')} value={MAIN_TABS.POSTS} />}
          {mediaType === 'game' && <Tab label={t('tabs.lists')} value={MAIN_TABS.LISTS} />}
          {mediaType === 'game' && <Tab label={t('tabs.gallery')} value={MAIN_TABS.GALLERY} />}
          {mediaType === 'game' && <Tab label={t('tabs.memories')} value={MAIN_TABS.MEMORIES} />}
          {mediaType === 'game' && <Tab label={t('tabs.playlogs')} value={MAIN_TABS.PLAYLOGS} />}
          {mediaType === 'game' && <Tab label={t('tabs.goty')} value={MAIN_TABS.GOTY} />}
          {mediaType === 'game' && <Tab label={t('tabs.activity')} value={MAIN_TABS.ACTIVITY} />}
          {mediaType === 'game' && <Tab label={t('tabs.follows')} value={MAIN_TABS.FOLLOWS} />}
        </Tabs>
      </Box>
      )}

      {/* ライブラリタブのコンテンツ */}
      {mainTabValue === MAIN_TABS.LIBRARY && (
        <Box sx={{ width: '100%' }}>
          {mediaType === 'game' && (
            <GameLibraryTab
              profile={profile}
              isPublic={isPublic}
              gameCounts={gameCounts}
              mainTabValue={mainTabValue}
            />
          )}
          {mediaType === 'anime' && (
            <AnimeLibraryTab
              profile={profile}
              isPublic={isPublic}
              animeCounts={animeCounts}
              mainTabValue={mainTabValue}
            />
          )}
          {mediaType === 'movie' && (
            <MovieLibraryTab
              profile={profile}
              isPublic={isPublic}
              movieCounts={movieCounts}
              mainTabValue={mainTabValue}
            />
          )}
        </Box>
      )}

      {/* コンテンツタブのコンテンツ */}
      {mainTabValue === MAIN_TABS.LISTS && (
        <Box>
          {!isPublic && (
            <StyledButton onClick={() => setCreateListDialogOpen(true)} startIcon={<PlaylistAddIcon />} sx={{ mb: 2 }}>
              新規リストを作成
            </StyledButton>
          )}
          
          <Grid container spacing={{ xs: 0.5, sm: 1, md: 1 }}>
            {gameLists.map((list) => (
              <GameListComponent
                key={list.id}
                list={list}
                onEdit={!isPublic ? handleEditList : undefined}
                onDelete={!isPublic ? handleDeleteList : undefined}
              />
            ))}
          </Grid>

          {listTotalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
              <Pagination
                count={listTotalPages}
                page={listPage}
                onChange={handleListPageChange}
              />
            </Box>
          )}

          <CreateGameList
            open={createListDialogOpen}
            onClose={() => setCreateListDialogOpen(false)}
            onListCreated={invalidateGameLists}
          />

          {editingList && (
            <EditGameList
              open={!!editingList}
              onClose={() => setEditingList(null)}
              list={editingList}
              onListUpdated={invalidateGameLists}
            />
          )}
        </Box>
      )}

      {/* GOTYタブのコンテンツ */}
      {mainTabValue === MAIN_TABS.GOTY && profile && (
        <GotyTab
          username={profile.username}
          isPublic={isPublic}
        />
      )}

      {/* レビュータブのコンテンツ */}
      {mainTabValue === MAIN_TABS.REVIEWS && (
        mediaType === 'anime' ? (
          <AnimeReviewsTab
            username={profile?.username}
            displayName={profile?.display_name}
            isPublic={isPublic}
          />
        ) : mediaType === 'movie' ? (
          <MovieReviewsTab
            username={profile?.username}
            displayName={profile?.display_name}
            isPublic={isPublic}
          />
        ) : (
          <ReviewsTab
            username={profile?.username}
            displayName={profile?.display_name}
            isPublic={isPublic}
          />
        )
      )}

      {/* メモリータブのコンテンツ */}
      {mainTabValue === MAIN_TABS.MEMORIES && (
        <MemoriesTab
          username={profile?.username}
          isPublic={isPublic}
          userProfile={profile}
        />
      )}

      {/* ポストタブのコンテンツ */}
      {mainTabValue === MAIN_TABS.POSTS && (
        <Grid container spacing={2}>
          <Grid item xs={12} sm={12} md={8.5}>
            <PostFeed
              username={profile?.username || ''}
              isMyPage={!isPublic}
              avatar={profile?.avatar}
              displayName={profile?.display_name}
              autoFocusComposer={queryParams.get('compose') === '1'}
            />
          </Grid>
        </Grid>
      )}

      {/* アクティビティタブのコンテンツ */}
      {mainTabValue === MAIN_TABS.ACTIVITY && (
        <ActivityTab 
          username={profile?.username} 
          isPublic={isPublic} 
        />
      )}

      {/* プロフィールタブのコンテンツ */}
      {mainTabValue === MAIN_TABS.PROFILE && mediaType === 'game' && (
        <ProfileTab
          profile={profile!}
          isPublic={isPublic}
        />
      )}
      {mainTabValue === MAIN_TABS.PROFILE && mediaType === 'anime' && (
        <AnimeProfileTab
          profile={profile!}
          isPublic={isPublic}
        />
      )}
      {mainTabValue === MAIN_TABS.PROFILE && mediaType === 'movie' && (
        <MovieProfileTab
          profile={profile!}
          isPublic={isPublic}
        />
      )}

      {/* フォロータブのコンテンツ */}
      {mainTabValue === MAIN_TABS.FOLLOWS && (
        <FollowsTab
          username={profile?.username}
          isPublic={isPublic}
        />
      )}

      {/* タイムラインタブのコンテンツ */}
      {mainTabValue === MAIN_TABS.TIMELINE && (
        <TimelineTab 
          username={profile?.username || ''} 
          isPublic={isPublic} 
        />
      )}

      {/* プレイログタブのコンテンツ */}
      {mainTabValue === MAIN_TABS.PLAYLOGS && profile && (
        <PlayLogsTab
          username={profile.username}
          isPublic={isPublic}
        />
      )}

      {/* ギャラリータブのコンテンツ */}
      {mainTabValue === MAIN_TABS.GALLERY && profile && (
        <GalleryTab
          username={profile.username}
          isPublic={isPublic}
        />
      )}

      <Snackbar
        open={openSnackbar}
        autoHideDuration={3000}
        onClose={() => setOpenSnackbar(false)}
        message={snackbarMessage}
      />

    </>
  );
};

export default ProfilePage;
